import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { toast } from "sonner";
import { Check, Clock, HelpCircle, Loader2, Mail, MessageSquare, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { submitContactMessage } from "@/lib/contact.functions";
import { useAuth } from "@/lib/auth-context";
import { FAQ_GROUPS } from "@/content/faq";

const TITLE = "Help and Contact | XAU Pro Academy";
const DESC = "Member help desk: frequently asked questions and direct contact with the XAU Pro Academy desk.";

export const Route = createFileRoute("/_authenticated/dashboard/support")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: SupportPage,
});

const TOPICS = [
  "Technical support",
  "Account and membership",
  "Platform and methodology",
  "Partnerships and media",
  "Other",
] as const;

const formSchema = z.object({
  name: z.string().trim().min(2, "Enter your name").max(100),
  email: z.string().trim().email("Enter a valid email address").max(255),
  topic: z.enum(TOPICS),
  message: z.string().trim().min(10, "Message must be at least 10 characters").max(2000),
});

function SupportPage() {
  const { profile, user } = useAuth();
  const [activeGroup, setActiveGroup] = useState(FAQ_GROUPS[0]?.id ?? "");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [topic, setTopic] = useState<string>("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (profile?.display_name) setName((v) => v || profile.display_name || "");
    if (user?.email) setEmail((v) => v || user.email || "");
  }, [profile?.display_name, user?.email]);

  const group = FAQ_GROUPS.find((g) => g.id === activeGroup) ?? FAQ_GROUPS[0];

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = formSchema.safeParse({ name, email, topic, message });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Check the form fields.");
      return;
    }
    setBusy(true);
    const res = await submitContactMessage({ data: { ...parsed.data, website: "" } }).catch(() => ({ ok: false }));
    setBusy(false);
    if (!res.ok) {
      toast.error("Could not send your message. Please email support@xauproacademy.com directly.");
      return;
    }
    setDone(true);
  };

  return (
    <div className="container mx-auto max-w-6xl px-4 py-10">
      <header className="max-w-2xl space-y-2">
        <div className="inline-flex items-center gap-2 rounded-full bg-gold-muted px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
          <HelpCircle className="h-3 w-3" /> Member Help Desk
        </div>
        <h1 className="font-display text-3xl font-bold md:text-4xl">
          Help &amp; <span className="text-gradient-gold">Contact</span>
        </h1>
        <p className="text-sm text-muted-foreground">
          Answers to common questions, and a direct line to the desk without leaving your workspace.
        </p>
      </header>

      <section className="mt-10">
        <div className="mb-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          <HelpCircle className="h-3.5 w-3.5 text-gold" /> Frequently asked questions
        </div>
        <div className="flex flex-wrap gap-2">
          {FAQ_GROUPS.map((g) => (
            <button
              key={g.id}
              type="button"
              onClick={() => setActiveGroup(g.id)}
              className={`rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                g.id === activeGroup
                  ? "bg-gradient-gold font-semibold text-gold-foreground"
                  : "bg-secondary/60 text-muted-foreground hover:text-foreground"
              }`}
            >
              {g.title}
            </button>
          ))}
        </div>
        <div className="mt-4 rounded-2xl bg-card/40 px-5 backdrop-blur-sm">
          <Accordion type="single" collapsible key={group?.id}>
            {group?.items.map((qa, i) => (
              <AccordionItem key={qa.q} value={`q-${i}`} className="border-border/30 last:border-b-0">
                <AccordionTrigger className="text-left text-sm font-medium hover:no-underline">
                  {qa.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                  {qa.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="mt-12 grid gap-6 lg:grid-cols-5">
        <div className="space-y-4 lg:col-span-2">
          <div className="mb-1 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <MessageSquare className="h-3.5 w-3.5 text-gold" /> Contact the desk
          </div>
          <InfoCard icon={Mail} title="Direct email">
            <a href="mailto:support@xauproacademy.com" className="hover:text-gold">support@xauproacademy.com</a>
          </InfoCard>
          <InfoCard icon={Clock} title="Support coverage">
            Monday to Friday. Replies typically within 24 business hours.
          </InfoCard>
          <InfoCard icon={ShieldCheck} title="Scope of support">
            Platform, account and technical matters. The desk does not provide personalized investment advice.
          </InfoCard>
        </div>

        <div className="rounded-2xl bg-card/40 p-6 backdrop-blur-sm lg:col-span-3">
          {done ? (
            <div className="flex min-h-64 flex-col items-center justify-center gap-3 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gold-muted">
                <Check className="h-6 w-6 text-gold" />
              </div>
              <p className="font-display text-lg font-semibold">Message received</p>
              <p className="max-w-sm text-sm text-muted-foreground">
                Your message has been relayed to the desk. Expect a reply at {email}.
              </p>
              <Button variant="ghost" size="sm" onClick={() => { setDone(false); setMessage(""); setTopic(""); }}>
                Send another message
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="sd-name">Name</Label>
                  <Input id="sd-name" maxLength={100} value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="sd-email">Email</Label>
                  <Input id="sd-email" type="email" maxLength={255} value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sd-topic">Topic</Label>
                <Select value={topic} onValueChange={setTopic}>
                  <SelectTrigger id="sd-topic"><SelectValue placeholder="Select a topic" /></SelectTrigger>
                  <SelectContent>
                    {TOPICS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sd-message">Message</Label>
                <Textarea id="sd-message" rows={6} maxLength={2000} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="How can the desk help?" />
              </div>
              <Button type="submit" variant="gold" className="w-full" disabled={busy}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send message"}
              </Button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function InfoCard({ icon: Icon, title, children }: { icon: typeof Mail; title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl bg-card/40 p-5 backdrop-blur-sm">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <Icon className="h-4 w-4 text-gold" /> {title}
      </div>
      <div className="mt-2 text-sm text-muted-foreground">{children}</div>
    </div>
  );
}
